import type { CameraData, CameraViewData } from '@/entities/model3d'
import type { Model } from '@/lib/three-d-resources/model'
import { exhaustive } from '@/lib/three-d-resources/utils'
import { THREE_D_FOV } from '@/lib/constants/model'
import { sum } from 'ramda'
import { Vector3, Vector4 } from 'three'

export interface SceneConfig {
  id: number | null
  title: string
  model: Model
  cameraViewData: CameraViewData
  apiData: CameraData | null
  visibleNodeIds: Set<number>
  isVisible: (nodeId: number) => boolean
  isLabelVisible: (labelId: string) => boolean
  calculateProgress: (progressMap: { [key: number]: number | { [key: number]: number } }) => number
  calculateHiddenLayerProgress: (progressMap: { [key: number]: number | { [key: number]: number } }) => number
  getHiddenLayerNames: () => string[]
  getVisibleLayerNames: () => string[]
  updateVisibleNodeIdsForV1Models(): void
}

export class CameraDataSceneConfig implements SceneConfig {
  id: number
  title: string
  model: Model
  cameraViewData: CameraViewData
  apiData: CameraData

  visibleNodeIds = new Set<number>()
  private visibleLabelIds = new Set<string>()

  constructor(model: Model, cameraData: CameraData) {
    this.id = cameraData.id
    this.title = cameraData.title
    this.model = model
    this.cameraViewData = cameraData.camera
    this.apiData = cameraData

    switch (model.apiData.version) {
      case 2:
        this.visibleNodeIds = new Set(cameraData.visible_nodes)
        break
      case 1:
        this.updateVisibleNodeIdsForV1Models()
        break
      default:
        exhaustive(model.apiData.version)
    }

    this.visibleLabelIds = new Set(cameraData.text_labels?.filter(l => l.is_visible).map(l => String(l.text_label_id)))
  }

  updateVisibleNodeIdsForV1Models() {
    if (this.model.apiData.version === 1) {
      this.visibleNodeIds = new Set(
        Array.from(this.model.apiLayersById.keys()).filter(n1 => this.apiData.hidden_nodes.every(n2 => n2.node_id !== n1))
      )
    }
  }

  isVisible = (nodeId: number): boolean => {
    return this.visibleNodeIds.has(nodeId)
  }

  isLabelVisible = (labelId: string): boolean => {
    return this.visibleLabelIds.has(labelId)
  }

  calculateProgress = calculateProgress

  calculateHiddenLayerProgress = calculateHiddenLayerProgress

  getHiddenLayerNames(): string[] {
    const allLayerIds = Array.from(this.model.layerNodesById.keys())
    const hiddenLayerIds = allLayerIds.filter(layer => !this.isVisible(layer))
    return hiddenLayerIds.map(id => this.model.nodeIdToName.get(id)!)
  }

  getVisibleLayerNames = getVisibleLayerNames
}

export class DefaultSceneConfig implements SceneConfig {
  id = null
  title = ''
  model: Model
  cameraViewData: CameraViewData
  apiData = null

  visibleNodeIds = new Set<number>()

  constructor(model: Model) {
    this.model = model
    this.cameraViewData = {
      fov: { x: THREE_D_FOV, y: THREE_D_FOV },
      position: model.sceneSize,
      target: new Vector3(0, 0, 0),
      orientation: new Vector4(0, 0, 0, 0),
    }

    this.visibleNodeIds = new Set(Array.from(model.apiLayersById.keys()))
  }

  isVisible = (_nodeId: number) => true

  isLabelVisible = (_nodeId: string) => true

  updateVisibleNodeIdsForV1Models() {}

  calculateProgress = calculateProgress

  calculateHiddenLayerProgress = calculateHiddenLayerProgress

  getHiddenLayerNames(): string[] {
    return []
  }

  getVisibleLayerNames = getVisibleLayerNames
}

function calculateProgress(
  this: SceneConfig,
  progressMap: {
    [key: number]: number | { [key: number]: number }
  }
): number {
  if (this.model.apiData.version === 1) {
    return (progressMap[this.model.id] as number) || 0
  }

  if (!this.visibleNodeIds.size) return 1

  const progresses = Array.from(this.visibleNodeIds).map(id => {
    return (progressMap[this.model.id] as { [key: number]: number })?.[id] || 0
  })

  return sum(progresses) / progresses.length
}

function getVisibleLayerNames(this: SceneConfig): string[] {
  return Array.from(this.visibleNodeIds).map(id => this.model.nodeIdToName.get(id)!)
}

function calculateHiddenLayerProgress(
  this: SceneConfig,
  progressMap: {
    [key: number]: number | { [key: number]: number }
  }
): number {
  if (this.model.apiData.version === 1) {
    return (progressMap[this.model.id] as number) || 0
  }
  const hiddenLayerIds = Array.from(this.model.apiLayersById.keys()).filter(layer => !this.isVisible(layer))

  if (!hiddenLayerIds.length) return 1

  const progresses = hiddenLayerIds.map(id => {
    return (progressMap[this.model.id] as { [key: number]: number })?.[id] || 0
  })

  return sum(progresses) / progresses.length
}
